import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import type { ApiSuccessResponse, ApiErrorResponse, PaginationMeta } from '../types/api';
export type { ListParams } from '../types/api';
import type { ListParams } from '../types/api';

const TOKEN_STORAGE_KEY = 'saletrax_access_token';
const REFRESH_TOKEN_KEY = 'saletrax_refresh_token';

const MAX_429_RETRIES = 3;
const BASE_BACKOFF_MS = 1000;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  failedQueue = [];
}

export function getStoredTokens() {
  return {
    accessToken: localStorage.getItem(TOKEN_STORAGE_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
  };
}

export function setStoredTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearStoredTokens() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

function resolveBaseURL(): string {
  try {
    const envUrl = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL);
    if (envUrl) return `${envUrl}/api/v1`;
  } catch { /* not in a Vite environment */ }
  return '/api/v1';
}

/** Use on `client.post(..., { timeout: GEMINI_HTTP_TIMEOUT_MS })` for routes that call Gemini (OCR, parsing, formula). */
export const GEMINI_HTTP_TIMEOUT_MS = 320_000;

export function createApiClient(baseURL: string = resolveBaseURL()): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      // Bypasses ngrok-free.dev browser interstitial when tunneling local backend during dev.
      // Harmless against any non-ngrok backend (just an unknown header).
      'ngrok-skip-browser-warning': 'true',
    },
  });

  // Request interceptor: attach access token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const { accessToken } = getStoredTokens();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor: retry with exponential backoff on 429
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiErrorResponse>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retryCount429?: number };

      if (error.response?.status === 429) {
        const retryCount = originalRequest._retryCount429 || 0;
        // Don't retry auth endpoints — surface rate limit errors immediately
        const isAuthEndpoint = originalRequest.url?.includes('/auth/');
        if (retryCount >= MAX_429_RETRIES || isAuthEndpoint) {
          return Promise.reject(error);
        }
        originalRequest._retryCount429 = retryCount + 1;

        // Use server-provided retryAfter or exponential backoff, capped at 10s
        const retryAfterHeader = error.response.headers['retry-after'];
        const retryAfterSec = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 0;
        const delayMs = Math.min(
          retryAfterSec > 0 ? retryAfterSec * 1000 : BASE_BACKOFF_MS * Math.pow(2, retryCount),
          10000 // Cap at 10 seconds
        );

        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return client(originalRequest);
      }

      return Promise.reject(error);
    }
  );

  // Response interceptor: auto-refresh on 401
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiErrorResponse>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // If not 401, or already retried, or it's the refresh endpoint itself, reject
      if (
        error.response?.status !== 401 ||
        originalRequest._retry ||
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/login')
      ) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue requests while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(client(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { refreshToken } = getStoredTokens();
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post<ApiSuccessResponse<{ tokens: { accessToken: string; refreshToken: string } }>>(
          `${baseURL}/auth/refresh`,
          { refreshToken }
        );

        const { accessToken: newAccess, refreshToken: newRefresh } = response.data.data.tokens;
        setStoredTokens(newAccess, newRefresh);
        processQueue(null, newAccess);

        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return client(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearStoredTokens();
        // Dispatch custom event for auth context to handle
        window.dispatchEvent(new CustomEvent('saletrax:auth:expired'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );

  return client;
}

// Default singleton instance
let defaultClient: AxiosInstance | null = null;

export function getApiClient(baseURL?: string): AxiosInstance {
  if (!defaultClient) {
    defaultClient = createApiClient(baseURL);
  }
  return defaultClient;
}

export function resetApiClient() {
  defaultClient = null;
}

// ═════════════════════════════════════════════════════════════════════════════
// Generic CRUD helpers
// ═════════════════════════════════════════════════════════════════════════════

export function buildQueryString(params: ListParams): string {
  const queryParts: string[] = [];
  if (params.page) queryParts.push(`page=${params.page}`);
  if (params.limit) queryParts.push(`limit=${params.limit}`);
  if (params.sort) queryParts.push(`sort=${params.sort}`);
  if (params.sortOrder) queryParts.push(`sortOrder=${params.sortOrder}`);
  if (params.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
  if (params.filter) {
    Object.entries(params.filter).forEach(([key, value]) => {
      queryParts.push(`filter[${key}]=${encodeURIComponent(String(value))}`);
    });
  }
  return queryParts.length ? `?${queryParts.join('&')}` : '';
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: PaginationMeta;
}

export async function fetchList<T>(
  client: AxiosInstance,
  endpoint: string,
  params: ListParams = {},
  axiosConfig?: AxiosRequestConfig
): Promise<PaginatedResponse<T>> {
  const qs = buildQueryString(params);
  const res = await client.get<ApiSuccessResponse<T[]>>(`${endpoint}${qs}`, axiosConfig);
  return {
    items: res.data.data,
    pagination: res.data.meta.pagination || {
      page: params.page || 1,
      limit: params.limit || 25,
      total: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
  };
}

export async function fetchOne<T>(client: AxiosInstance, endpoint: string, id: string): Promise<T> {
  const res = await client.get<ApiSuccessResponse<T>>(`${endpoint}/${id}`);
  return res.data.data;
}

export async function createOne<T, D = Partial<T>>(client: AxiosInstance, endpoint: string, data: D): Promise<T> {
  const res = await client.post<ApiSuccessResponse<T>>(endpoint, data);
  return res.data.data;
}

export async function updateOne<T, D = Partial<T>>(client: AxiosInstance, endpoint: string, id: string, data: D): Promise<T> {
  const res = await client.put<ApiSuccessResponse<T>>(`${endpoint}/${id}`, data);
  return res.data.data;
}

export async function patchOne<T, D = Partial<T>>(client: AxiosInstance, endpoint: string, id: string, data: D): Promise<T> {
  const res = await client.patch<ApiSuccessResponse<T>>(`${endpoint}/${id}`, data);
  return res.data.data;
}

export async function deleteOne(client: AxiosInstance, endpoint: string, id: string): Promise<void> {
  await client.delete(`${endpoint}/${id}`);
}
